import { CreditCard, KeyRound, Package, RotateCcw, Wrench } from 'lucide-react';

// Help Center content. Answers follow the Terms (sections 5–10), the Refund
// Policy (sections 5, 6, 8), the home page FAQ and the profile/checkout screens.
// Keep them in sync when those change.
//
// An answer is a list of paragraphs. A paragraph is a string, or a list of
// strings and links: { to: '/path', label } for pages, { href, label } for mailto/tel.
// `tags` are extra search words that don't appear in the text.

const ORDERS = { to: '/profile', label: 'Your orders' };
const PROFILE = { to: '/profile', label: 'profile' };

export const HELP_TOPICS = [
  {
    id: 'orders',
    label: 'Orders & delivery',
    icon: Package,
    articles: [
      {
        id: 'get-my-project',
        q: 'How do I get my project after paying?',
        a: [
          [
            'Orders are typically processed within 12 hours of payment. Once processed, you get download links or access details by email, and the downloads also appear on the order in ',
            ORDERS,
            '.',
          ],
        ],
        tags: 'download delivery access link receive',
      },
      {
        id: 'order-not-arrived',
        q: "My project hasn't arrived",
        a: [
          [
            'First check your spam or promotions folder for an email from us. Then open the order in ',
            ORDERS,
            ': downloads show up there as soon as it’s processed.',
          ],
          'Still nothing after 12 hours? Message us with your order ID and we’ll look into it.',
        ],
        tags: 'missing not received late delay waiting download email',
      },
      {
        id: 'find-order-id',
        q: 'Where do I find my order ID?',
        a: [
          [
            'Every order in ',
            ORDERS,
            ' shows its ID. Open an order to see its details, timeline and downloads.',
          ],
        ],
        tags: 'order number reference track status',
      },
      {
        id: 'whats-included',
        q: 'What’s included with a project?',
        a: [
          'Every project is a complete source code bundle. Each project page lists exactly what’s included, such as documentation, database design, setup videos or a project report.',
        ],
        action: { to: '/products', label: 'Browse projects' },
        tags: 'source code documentation report video files',
      },
    ],
  },
  {
    id: 'payments',
    label: 'Payments & invoices',
    icon: CreditCard,
    articles: [
      {
        id: 'how-to-pay',
        q: 'How do I pay?',
        a: [
          'Checkout uses UPI. Scan the QR code shown at checkout with any UPI app and complete the payment. We verify it before unlocking your project. All prices are in Indian Rupees.',
        ],
        tags: 'upi qr code gpay phonepe paytm card bank price rupees inr',
      },
      {
        id: 'payment-pending',
        q: 'I paid, but my order still says pending',
        a: [
          'We verify every payment before unlocking a project, so an order can stay pending for a while after you pay. If it’s still pending after 12 hours, message us with your order ID and the UPI transaction ID from your payment app.',
        ],
        tags: 'payment verification status stuck processing upi',
      },
      {
        id: 'charged-twice',
        q: 'I was charged twice',
        a: [
          'If you were charged twice for one order, that’s a billing error on our side and we’ll correct it. Send us your order ID and the UPI transaction IDs for both payments.',
          [
            'Buying the same project again in a separate order isn’t covered. See the ',
            { to: '/refund', label: 'refund policy' },
            '.',
          ],
        ],
        tags: 'double duplicate payment deducted debited extra',
      },
      {
        id: 'gst-invoice',
        q: 'Can I get a GST invoice?',
        a: [
          [
            'Yes. Once your invoice is issued, it appears on the order’s details page in ',
            ORDERS,
            '. The state you enter at checkout sets the place of supply on the invoice.',
          ],
        ],
        tags: 'gst invoice bill receipt tax gstin',
      },
    ],
  },
  {
    id: 'refunds',
    label: 'Refunds & cancellations',
    icon: RotateCcw,
    articles: [
      {
        id: 'refund',
        q: 'Can I get a refund?',
        a: [
          'No. Projects are digital and can be downloaded as soon as they’re delivered, so all sales are final. Check the project details, or ask us, before you buy.',
        ],
        action: { to: '/refund', label: 'Read the refund policy' },
        tags: 'refund money back return',
      },
      {
        id: 'cancel-order',
        q: 'Can I cancel an order?',
        a: [
          [
            'A completed purchase can’t be cancelled. If your order is still pending, open it in ',
            ORDERS,
            ': when cancelling is possible, you’ll see a Cancel Order button there.',
          ],
        ],
        tags: 'cancel cancellation stop',
      },
      {
        id: 'not-working',
        q: 'The project doesn’t work',
        a: [
          'If a project has a significant defect that makes it unusable and wasn’t disclosed in its description, we’ll work to fix it or provide an alternative.',
          'Message us with your order ID, what you expected and what happened. Error messages and screenshots help.',
        ],
        tags: 'bug error broken crash defect issue problem',
      },
      {
        id: 'chargeback',
        q: 'Should I file a chargeback with my bank?',
        a: [
          'Please talk to us first. Most problems are quicker to fix directly. Under our refund policy, a chargeback on a delivered project can close your account and remove access to everything you’ve bought.',
        ],
        tags: 'dispute bank chargeback',
      },
    ],
  },
  {
    id: 'account',
    label: 'Account & GitHub access',
    icon: KeyRound,
    articles: [
      {
        id: 'github-access',
        q: 'How do I get access to a GitHub repository I bought?',
        a: [
          [
            'Add your GitHub username in Account settings on your ',
            PROFILE,
            '. Repository access for your purchases goes to that GitHub account.',
          ],
        ],
        tags: 'github repo repository invite collaborator username',
      },
      {
        id: 'change-github',
        q: 'Can I change the GitHub account that has access?',
        a: [
          'Yes, in Account settings. Access for your purchases moves to the new account. You can change it once every 30 days.',
        ],
        tags: 'github username switch update repository',
      },
      {
        id: 'no-emails',
        q: 'I’m not getting emails from you',
        a: [
          [
            'Make sure your account has an email address. It’s where receipts, delivery emails and password reset codes go, and you can add one from your ',
            PROFILE,
            '.',
          ],
          'Then check your spam or promotions folder, and mark our emails as “not spam” so the next ones land in your inbox.',
        ],
        tags: 'mail inbox spam receipt password reset',
      },
      {
        id: 'share-resell',
        q: 'Can I share or resell a project I bought?',
        a: [
          'No. A purchase gives you a license to use the project, not ownership of it. You can’t resell, redistribute or share it, or present the code as your own.',
        ],
        action: { to: '/terms', label: 'Read the license terms' },
        tags: 'license resell share redistribute copy',
      },
    ],
  },
  {
    id: 'support',
    label: 'Setup & custom work',
    icon: Wrench,
    articles: [
      {
        id: 'setup-help',
        q: 'Will you help me set up a project?',
        a: [
          'Yes. We offer free technical support to help you get started with your purchase. How long support lasts depends on the project and is listed on its page.',
        ],
        tags: 'install run deploy setup configure support help',
      },
      {
        id: 'customization',
        q: 'Is customization included?',
        a: [
          'Not unless the project page says so. Changes to a project are handled as a custom build.',
        ],
        action: { to: '/request-project', label: 'Request a custom build' },
        tags: 'modify change feature customise',
      },
      {
        id: 'custom-build',
        q: 'Can you build a project from scratch?',
        a: [
          [
            'Yes. Describe what you need on the ',
            { to: '/request-project', label: 'custom build page' },
            ' and we’ll get back to you.',
          ],
        ],
        tags: 'custom new build develop scratch',
      },
    ],
  },
];

export const HELP_ARTICLES = HELP_TOPICS.flatMap(({ articles, ...topic }) =>
  articles.map(article => ({ ...article, topic }))
);
